import { DIRECTIONS } from '@btime/constants'
import {
  State,
  appendValueOnIndex,
  duplicateValueWithIndex,
  moveTo,
  moveToPath,
  removeValueWith,
  removeValueWithIndex,
  setValue
} from '@btime/state-monad'
import {
  assoc,
  assocPath,
  compose,
  concat,
  curry,
  dissoc,
  find,
  findIndex,
  is,
  last,
  map,
  mapObjIndexed,
  path as pathFunc,
  pickBy,
  pipe,
  prop,
  propEq,
  sortBy,
  splitAt,
  toLower
} from 'ramda'
import { propOrPath, uniqId } from '@btime/helpers'
import { useReducer, useCallback } from 'react'
import moment from 'moment'

const SET_FORM = 'SET_FORM'
const MERGE_LIST_IN = 'MERGE_LIST_IN'
const CHANGE_VALUE_IN = 'CHANGE_VALUE_IN'
const DUPLICATE_IN = 'DUPLICATE_IN'
const REMOVE_IN = 'REMOVE_IN'
const MOVE_CLOSE_TO = 'MOVE_CLOSE_TO'
const ADD_IN = 'ADD_IN'
const SORT_BY_IN = 'SORT_BY_IN'
const SET_ERRORS = 'SET_ERRORS'
const MARK_SELECTED = 'MARK_SELECTED'
const TOGGLE_MULTI_EDIT = 'TOGGLE_MULTI_EDIT'
const TOGGLE_ALL_MULTI_EDIT = 'TOGGLE_ALL_MULTI_EDIT'

const defaultState = {}

export const reducer = (state, action) => {
  switch (action.type) {
    case SET_FORM:
      return action.payload

    case MERGE_LIST_IN:
      var { path, value } = action.payload

      return State.of(state)
        .map(moveToPath(path))
        .map(setValue(list => concat(list, value)))
        .fold()

    case CHANGE_VALUE_IN:
      var { path: fullPath, value } = action.payload

      const [listPath, recordPath, valuePath, isMultiEditing] = fullPath.reduce(
        ([listPath, recordPath, valuePath, isMultiEditing], key, index) => {
          if (isMultiEditing) return [listPath, recordPath, valuePath, isMultiEditing]

          if (pathFunc([...recordPath, key, '__multiEdit'], state)) {
            return [recordPath, [...recordPath, key], fullPath.splice(index + 1), true]
          }

          return [listPath, [...recordPath, key], valuePath, isMultiEditing]
        },
        [[], [], [], false]
      )

      var record = pathFunc(recordPath, state) || {}
      var list = pathFunc(listPath, state) || []

      if (isMultiEditing) {
        return State.of(state) //
          .map(moveToPath(listPath))
          .map(
            setValue(
              map(item => {
                if (!item.__multiEdit) return item
                return assocPath(valuePath, value, item)
              })
            )
          )
          .fold()
      }

      return State.of(state) //
        .map(moveToPath(fullPath))
        .map(setValue(value))
        .fold()

    case REMOVE_IN:
      var [path, [index]] = splitAt(-1, action.payload)

      return State.of(cleanSelecteds(path, state)) //
        .map(moveToPath(path))
        .map(removeValueWithIndex(index))
        .fold()

    case DUPLICATE_IN:
      var [path, [index]] = splitAt(-1, action.payload)

      return State.of(state) //
        .map(moveToPath(path))
        .map(duplicateValueWithIndex(index))
        .map(moveToPath([index + 1, '__multiEdit']))
        .map(setValue(false))
        .fold()

    case ADD_IN:
      var { path, value } = action.payload
      if (path && is(Number, last(path))) {
        var [path, [index]] = splitAt(-1, path)
      }

      return State.of(cleanSelecteds(path, state)) //
        .map(moveToPath(path))
        .map(appendValueOnIndex(index, { ...value, reference: uniqId(), __selected: true, __multEdit: false }))
        .fold()

    case MOVE_CLOSE_TO:
      var { path, fromReference, toReference, direction } = action.payload

      const fromItem = compose(find(propEq('reference', fromReference)), pathFunc(path))(state)

      const stateWithoutFrom = State.of(state)
        .map(moveToPath(path))
        .map(removeValueWith(propEq('reference', fromReference)))
        .fold()

      let toIndex = compose(findIndex(propEq('reference', toReference)), pathFunc(path))(stateWithoutFrom)
      if (direction === DIRECTIONS.DOWN) {
        toIndex += 1
      }

      return State.of(stateWithoutFrom) //
        .map(moveToPath(path))
        .map(appendValueOnIndex(toIndex, fromItem))
        .fold()

    case SORT_BY_IN:
      var { path, value } = action.payload

      return State.of(state)
        .map(moveToPath(path))
        .map(sortValue(value || 'name'))
        .fold()

    case SET_ERRORS:
      let formattedState = cleanErrors(state)
      action.payload.map(({ path, message }) => {
        var [path, [field]] = splitAt(-1, path)

        formattedState = State.of(formattedState) //
          .map(moveToPath(path))
          .map(moveTo(field + '__error'))
          .map(setValue(message))
          .fold()
      })

      return formattedState

    case MARK_SELECTED:
      var [path] = splitAt(-1, action.payload)

      return State.of(cleanSelecteds(path, state)) //
        .map(moveToPath(action.payload))
        .map(moveTo('__selected'))
        .map(setValue(true))
        .fold()

    case TOGGLE_MULTI_EDIT:
      var value = pathFunc([...action.payload, '__multiEdit'], state)

      return State.of(state) //
        .map(moveToPath(action.payload))
        .map(moveTo('__multiEdit'))
        .map(setValue(!value))
        .fold()

    case TOGGLE_ALL_MULTI_EDIT:
      var list = pathFunc(action.payload, state)
      var isAllSelected = list.every(prop('__multiEdit'))

      return pipe(
        map(assoc('__multiEdit', !isAllSelected)), //
        list => assocPath(action.payload, list, state)
      )(list)

    default:
      return state
  }
}

const cleanSelecteds = (path, state) => {
  return pipe(
    pathFunc(path), //
    map(dissoc('__selected')),
    list => assocPath(path, list, state)
  )(state)
}

const cleanErrors = state => {
  if (!is(Object, state)) return state
  if (moment.isMoment(state)) return state
  if (moment.isDate(state)) return state
  if (is(Array, state) && !!state.map) {
    return state.map(cleanErrors)
  }
  return pipe(
    pickBy((value, key) => !key.includes('__error')), //
    mapObjIndexed(value => cleanErrors(value))
  )(state)
}

const sortValue = path => setValue(value => sortBy(compose(toLower, propOrPath('£££££££££££££££££', path)), value))

export const useFormReducer = (initialState = {}) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const setForm = useCallback(payload => dispatch({ payload, type: SET_FORM }), [dispatch])
  const mergeListIn = useCallback(
    (path, value) => dispatch({ payload: { path, value }, type: MERGE_LIST_IN }),
    [dispatch]
  )
  const addIn = useCallback((path, value) => dispatch({ payload: { path, value }, type: ADD_IN }), [dispatch])

  const handleChangeIn = useCallback(
    curry((path, value) => dispatch({ payload: { path, value }, type: CHANGE_VALUE_IN })),
    [dispatch]
  )
  const handleChange = useCallback(
    curry((field, value) => dispatch({ payload: { path: [field], value }, type: CHANGE_VALUE_IN })),
    [dispatch]
  )
  const removeIn = useCallback(path => dispatch({ payload: path, type: REMOVE_IN }), [dispatch])
  const duplicateIn = useCallback(path => dispatch({ payload: path, type: DUPLICATE_IN }), [dispatch])
  const moveCloseTo = useCallback(
    (path, fromReference, toReference, direction) =>
      dispatch({ payload: { path, fromReference, toReference, direction }, type: MOVE_CLOSE_TO }),
    [dispatch]
  )
  const sortListBy = useCallback((path, value) => dispatch({ payload: { path, value }, type: SORT_BY_IN }), [dispatch])
  const setErrors = useCallback(errors => dispatch({ payload: errors, type: SET_ERRORS }), [dispatch])
  const markSelected = useCallback(path => dispatch({ payload: path, type: MARK_SELECTED }), [dispatch])
  const toggleMultiEdit = useCallback(path => dispatch({ payload: path, type: TOGGLE_MULTI_EDIT }), [dispatch])
  const toggleAllMultiEdit = useCallback(path => dispatch({ payload: path, type: TOGGLE_ALL_MULTI_EDIT }), [dispatch])

  return [
    state,
    {
      setForm,
      mergeListIn,
      addIn,
      handleChangeIn,
      handleChange,
      removeIn,
      duplicateIn,
      moveCloseTo,
      sortListBy,
      setErrors,
      markSelected,
      toggleMultiEdit,
      toggleAllMultiEdit
    }
  ]
}
